/**
 * @module SettingsNotifications
 * @description Push notification settings with diagnostic status indicator,
 * iOS PWA guide, test button, and smart notification preferences.
 */
import React, { useState, useEffect, memo, useCallback } from 'react';
import { useAuth, useLanguage, api } from '../../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Loader2, Bell, BellOff, Clock, CheckCircle, XCircle, AlertTriangle, Send, Smartphone, Info } from 'lucide-react';
import { toast } from 'sonner';
import SmartNotificationPreferences from '../NotificationPreferences';

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function detectiOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

function isStandalonePWA() {
  return window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true;
}

const StatusBadge = ({ status, label }) => {
  const config = {
    active: { icon: CheckCircle, color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/30' },
    blocked: { icon: XCircle, color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/30' },
    unsupported: { icon: AlertTriangle, color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/30' },
    inactive: { icon: BellOff, color: 'text-zinc-400', bg: 'bg-zinc-500/10 border-zinc-500/30' },
    ios_needs_pwa: { icon: Smartphone, color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/30' },
  };
  const c = config[status] || config.inactive;
  const Icon = c.icon;
  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-semibold uppercase tracking-wider ${c.bg}`} data-testid="notification-status-badge">
      <Icon className={`w-3.5 h-3.5 ${c.color}`} />
      <span className={c.color}>{label}</span>
    </div>
  );
};

const SettingsNotifications = memo(function SettingsNotifications() {
  const { user } = useAuth();
  const { language, t } = useLanguage();

  const [pushStatus, setPushStatus] = useState('checking'); // checking | active | inactive | blocked | unsupported | ios_needs_pwa
  const [notifSubscribed, setNotifSubscribed] = useState(false);
  const [notifSettings, setNotifSettings] = useState({
    enabled: true,
    breakfast_time: '08:00',
    lunch_time: '12:00',
    dinner_time: '19:00',
    weekly_summary: true
  });
  const [loading, setLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [serverDiag, setServerDiag] = useState(null);

  const fr = language === 'fr';

  // Compute full diagnostic status
  const diagnose = useCallback(async () => {
    // 1. Browser support
    const swSupported = 'serviceWorker' in navigator;
    const pushSupported = 'PushManager' in window;
    const notifSupported = 'Notification' in window;

    if (!swSupported || !pushSupported || !notifSupported || !VAPID_PUBLIC_KEY) {
      setPushStatus('unsupported');
      return;
    }

    // 2. iOS without PWA mode
    if (detectiOS() && !isStandalonePWA()) {
      setPushStatus('ios_needs_pwa');
      return;
    }

    // 3. Browser permission
    const perm = Notification.permission;
    if (perm === 'denied') {
      setPushStatus('blocked');
      return;
    }

    // 4. Check existing subscription
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        setNotifSubscribed(true);
        setPushStatus('active');
        // Fetch settings
        try {
          const res = await api.get('/notifications/settings');
          if (res.data) setNotifSettings(prev => ({ ...prev, ...res.data }));
        } catch (_) {}
      } else {
        setNotifSubscribed(false);
        setPushStatus('inactive');
      }
    } catch (err) {
      console.error('[Notif] Diagnosis error:', err);
      setPushStatus('inactive');
    }
  }, []);

  useEffect(() => {
    // Register SW then diagnose
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js')
        .then(() => diagnose())
        .catch(() => setPushStatus('unsupported'));
    } else {
      setPushStatus('unsupported');
    }
  }, [diagnose]);

  // Subscribe
  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') {
        setPushStatus('blocked');
        toast.error(fr ? 'Permission refusée. Vérifiez les réglages de votre navigateur.' : 'Permission denied. Check browser settings.');
        setLoading(false);
        return;
      }

      const registration = await navigator.serviceWorker.ready;
      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
        });
      }

      const subData = subscription.toJSON();
      await api.post('/notifications/subscribe', {
        endpoint: subData.endpoint,
        keys: subData.keys
      });

      setNotifSubscribed(true);
      setPushStatus('active');
      toast.success(fr ? 'Notifications activées!' : 'Notifications enabled!');
    } catch (err) {
      console.error('[Notif] Subscribe error:', err);
      toast.error(fr ? 'Erreur d\'activation des notifications' : 'Error enabling notifications');
    } finally {
      setLoading(false);
    }
  };

  // Unsubscribe
  const handleUnsubscribe = async () => {
    setLoading(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        const endpoint = subscription.endpoint;
        await subscription.unsubscribe();
        await api.delete(`/notifications/unsubscribe?endpoint=${encodeURIComponent(endpoint)}`);
      }
      setNotifSubscribed(false);
      setPushStatus('inactive');
      toast.success(fr ? 'Notifications desactivees' : 'Notifications disabled');
    } catch (err) {
      console.error('[Notif] Unsubscribe error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Test push
  const handleTestPush = async () => {
    setTestLoading(true);
    setTestResult(null);
    try {
      const res = await api.post('/notifications/test-push');
      setTestResult(res.data);
      if (res.data.success) {
        toast.success(fr ? 'Notification test envoyee! Vérifiez votre telephone.' : 'Test notification sent! Check your phone.');
      } else {
        toast.error(fr ? `Echec: ${res.data.reason}` : `Failed: ${res.data.reason}`);
      }
    } catch (err) {
      setTestResult({ success: false, reason: 'network_error' });
      toast.error(fr ? 'Erreur reseau' : 'Network error');
    } finally {
      setTestLoading(false);
    }
  };

  // Fetch server diagnostics
  const fetchDiag = async () => {
    try {
      const res = await api.get('/notifications/infrastructure-check');
      setServerDiag(res.data);
    } catch (_) {}
  };

  const handleUpdate = async (newSettings) => {
    const merged = { ...notifSettings, ...newSettings };
    setNotifSettings(merged);
    try {
      await api.put('/notifications/settings', merged);
    } catch (e) {
      console.error('Error updating notification settings:', e);
    }
  };

  // Status labels
  const statusLabels = {
    checking: fr ? 'Verification...' : 'Checking...',
    active: fr ? 'Actives' : 'Active',
    inactive: fr ? 'Inactives' : 'Inactive',
    blocked: fr ? 'Bloquees par le navigateur' : 'Blocked by browser',
    unsupported: fr ? 'Non supportees' : 'Not supported',
    ios_needs_pwa: fr ? 'Ajoutez a l\'ecran d\'accueil' : 'Add to Home Screen',
  };

  return (
    <div className="space-y-4">
      {/* Main Notification Card */}
      <Card className="bg-card border-border">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <CardTitle className="font-heading text-xl uppercase tracking-tight flex items-center gap-2">
                <Bell className="w-5 h-5 text-primary" />
                {fr ? 'Notifications Push' : 'Push Notifications'}
              </CardTitle>
              <CardDescription>
                {fr ? 'Rappels de repas et alertes en temps reel' : 'Meal reminders and real-time alerts'}
              </CardDescription>
            </div>
            {pushStatus !== 'checking' && (
              <StatusBadge status={pushStatus} label={statusLabels[pushStatus]} />
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Loading state */}
          {pushStatus === 'checking' && (
            <div className="flex items-center gap-3 text-muted-foreground" data-testid="notification-checking">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="text-sm">{fr ? 'Verification du système...' : 'Checking system...'}</span>
            </div>
          )}

          {/* UNSUPPORTED */}
          {pushStatus === 'unsupported' && (
            <div className="p-4 rounded-lg bg-amber-500/5 border border-amber-500/20" data-testid="notification-unsupported">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-amber-300 mb-1">
                    {fr ? 'Navigateur non compatible' : 'Browser not compatible'}
                  </p>
                  <p className="text-muted-foreground">
                    {fr
                      ? 'Utilisez Chrome, Edge, Firefox ou Safari (iOS 16.4+) pour recevoir les notifications push.'
                      : 'Use Chrome, Edge, Firefox or Safari (iOS 16.4+) to receive push notifications.'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* iOS NEEDS PWA */}
          {pushStatus === 'ios_needs_pwa' && (
            <div className="p-4 rounded-lg bg-blue-500/5 border border-blue-500/20" data-testid="notification-ios-pwa-guide">
              <div className="flex items-start gap-3">
                <Smartphone className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
                <div className="text-sm space-y-2">
                  <p className="font-medium text-blue-300">
                    {fr ? 'Etape requise sur iPhone/iPad' : 'Required step on iPhone/iPad'}
                  </p>
                  <p className="text-muted-foreground">
                    {fr
                      ? 'Sur iOS, les notifications push necessitent que l\'app soit ajoutee a votre ecran d\'accueil.'
                      : 'On iOS, push notifications require the app to be added to your home screen.'}
                  </p>
                  <div className="bg-secondary/50 p-3 rounded-md space-y-1">
                    <p className="font-medium text-foreground text-xs uppercase tracking-wider">
                      {fr ? 'Comment faire :' : 'How to:'}
                    </p>
                    <ol className="text-muted-foreground list-decimal list-inside space-y-1">
                      <li>{fr ? 'Appuyez sur le bouton Partager (rectangle avec fleche)' : 'Tap the Share button (rectangle with arrow)'}</li>
                      <li>{fr ? 'Faites defiler et appuyez "Sur l\'ecran d\'accueil"' : 'Scroll down and tap "Add to Home Screen"'}</li>
                      <li>{fr ? 'Confirmez en appuyant "Ajouter"' : 'Confirm by tapping "Add"'}</li>
                      <li>{fr ? 'Ouvrez YUMIX depuis votre ecran d\'accueil' : 'Open YUMIX from your home screen'}</li>
                      <li>{fr ? 'Revenez ici pour activer les notifications' : 'Come back here to enable notifications'}</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* BLOCKED */}
          {pushStatus === 'blocked' && (
            <div className="p-4 rounded-lg bg-red-500/5 border border-red-500/20" data-testid="notification-blocked">
              <div className="flex items-start gap-3">
                <XCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                <div className="text-sm space-y-2">
                  <p className="font-medium text-red-300">
                    {fr ? 'Notifications bloquees par votre navigateur' : 'Notifications blocked by your browser'}
                  </p>
                  <p className="text-muted-foreground">
                    {fr
                      ? 'Vous avez refuse la permission. Pour reactiver :'
                      : 'You denied permission. To re-enable:'}
                  </p>
                  <div className="bg-secondary/50 p-3 rounded-md text-muted-foreground">
                    {detectiOS() ? (
                      <p>{fr ? 'Allez dans Réglages > Safari > Notifications > YUMIX > Autoriser' : 'Go to Settings > Safari > Notifications > YUMIX > Allow'}</p>
                    ) : (
                      <p>{fr ? 'Cliquez sur l\'icone cadenas dans la barre d\'adresse > Notifications > Autoriser' : 'Click the lock icon in the address bar > Notifications > Allow'}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* INACTIVE — show subscribe button */}
          {pushStatus === 'inactive' && (
            <div className="flex items-center justify-between p-4 rounded-lg bg-secondary/50 border border-border" data-testid="notification-inactive">
              <div className="flex items-center gap-3">
                <BellOff className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">{fr ? 'Notifications desactivees' : 'Notifications disabled'}</p>
                  <p className="text-xs text-muted-foreground">
                    {fr ? 'Activez pour recevoir des rappels de repas et alertes' : 'Enable to receive meal reminders and alerts'}
                  </p>
                </div>
              </div>
              <Button onClick={handleSubscribe} disabled={loading} data-testid="notification-enable-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (fr ? 'Activer' : 'Enable')}
              </Button>
            </div>
          )}

          {/* ACTIVE — show settings + test button */}
          {pushStatus === 'active' && (
            <div className="space-y-4">
              {/* Active confirmation + disable */}
              <div className="flex items-center justify-between p-4 rounded-lg bg-emerald-500/5 border border-emerald-500/20" data-testid="notification-active">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                  <div>
                    <p className="text-sm font-medium text-emerald-300">{fr ? 'Système operationnel' : 'System operational'}</p>
                    <p className="text-xs text-muted-foreground">
                      {fr ? 'Tu recevras des rappels de repas et alertes coaching' : 'You will receive meal reminders and coaching alerts'}
                    </p>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={handleUnsubscribe} disabled={loading} data-testid="notification-disable-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (fr ? 'Desactiver' : 'Disable')}
                </Button>
              </div>

              {/* Test push button */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/30 border border-border">
                <div className="flex items-center gap-3">
                  <Send className="w-4 h-4 text-primary" />
                  <div>
                    <p className="text-sm font-medium">{fr ? 'Tester les notifications' : 'Test notifications'}</p>
                    <p className="text-xs text-muted-foreground">
                      {fr ? 'Envoie une notification test a ce telephone' : 'Send a test notification to this device'}
                    </p>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={handleTestPush} disabled={testLoading} data-testid="test-push-btn">
                  {testLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (fr ? 'Tester' : 'Test')}
                </Button>
              </div>

              {/* Test result */}
              {testResult && (
                <div className={`p-3 rounded-lg text-sm flex items-center gap-2 ${testResult.success ? 'bg-emerald-500/10 text-emerald-300' : 'bg-red-500/10 text-red-300'}`} data-testid="test-push-result">
                  {testResult.success ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                  <span>
                    {testResult.success
                      ? (fr ? 'Notification envoyee! Vérifiez votre telephone.' : 'Notification sent! Check your phone.')
                      : (fr ? `Echec: ${testResult.reason === 'no_subscriptions' ? 'Aucun abonnement actif' : 'Erreur d\'envoi'}` : `Failed: ${testResult.reason}`)
                    }
                  </span>
                </div>
              )}

              {/* Meal reminder times */}
              <div className="grid grid-cols-3 gap-4 pt-3 border-t border-border">
                {[
                  { key: 'breakfast_time', label_fr: 'Dejeuner', label_en: 'Breakfast', testid: 'breakfast-time-input' },
                  { key: 'lunch_time', label_fr: 'Diner', label_en: 'Lunch', testid: 'lunch-time-input' },
                  { key: 'dinner_time', label_fr: 'Souper', label_en: 'Dinner', testid: 'dinner-time-input' },
                ].map(t => (
                  <div key={t.key} className="space-y-2">
                    <Label className="flex items-center gap-2 text-xs">
                      <Clock className="w-3.5 h-3.5" />
                      {fr ? t.label_fr : t.label_en}
                    </Label>
                    <Input
                      type="time"
                      value={notifSettings[t.key]}
                      onChange={(e) => handleUpdate({ [t.key]: e.target.value })}
                      className="bg-secondary border-input"
                      data-testid={t.testid}
                    />
                  </div>
                ))}
              </div>

              {/* Weekly summary */}
              <div className="flex items-center justify-between pt-3 border-t border-border">
                <div>
                  <Label>{fr ? 'Resume hebdomadaire' : 'Weekly summary'}</Label>
                  <p className="text-xs text-muted-foreground">
                    {fr ? 'Recois un recap chaque dimanche' : 'Receive a recap every Sunday'}
                  </p>
                </div>
                <Switch
                  checked={notifSettings.weekly_summary}
                  onCheckedChange={(checked) => handleUpdate({ weekly_summary: checked })}
                  data-testid="weekly-summary-switch"
                />
              </div>
            </div>
          )}

          {/* Server diagnostics (coach only) */}
          {user?.role && ['coach', 'super_admin', 'admin'].includes(user.role) && (
            <div className="pt-3 border-t border-border">
              <Button variant="ghost" size="sm" className="text-xs text-muted-foreground" onClick={fetchDiag} data-testid="server-diag-btn">
                <Info className="w-3.5 h-3.5 mr-1" />
                {fr ? 'Diagnostic serveur' : 'Server diagnostics'}
              </Button>
              {serverDiag && (
                <div className="mt-2 p-3 rounded-lg bg-secondary/30 text-xs space-y-1 font-mono" data-testid="server-diag-results">
                  <p>Push VAPID: <span className={serverDiag.web_push?.vapid_key_configured ? 'text-emerald-400' : 'text-red-400'}>{serverDiag.web_push?.status}</span></p>
                  <p>Email: <span className={serverDiag.email?.key_valid ? 'text-emerald-400' : 'text-amber-400'}>{serverDiag.email?.status}</span></p>
                  <p>{fr ? 'Abonnements push actifs' : 'Active push subs'}: {serverDiag.user_subscriptions?.total_active_subscriptions || 0}</p>
                  <p>{fr ? 'Utilisateurs avec push' : 'Users with push'}: {serverDiag.user_subscriptions?.users_with_push_enabled || 0}</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Smart Notification Preferences (only when active) */}
      {pushStatus === 'active' && (
        <SmartNotificationPreferences />
      )}

      {/* 🔊 Sons de célébration — toggle local, pas de backend */}
      <CelebrationSoundToggle />
    </div>
  );
});

/**
 * Local toggle (localStorage) for the in-app celebration cue played by
 * the EXTRA MILES hero pop-in on a successful WHO-compliant scan.
 * Pure client-side, no backend round-trip.
 */
const CelebrationSoundToggle = memo(function CelebrationSoundToggle() {
  const { language } = useLanguage();
  const fr = (language || 'fr').startsWith('fr');
  const [muted, setMuted] = useState(() => {
    try { return localStorage.getItem('yumix_sound_muted') === '1'; } catch { return false; }
  });
  const handleToggle = useCallback(async (next) => {
    setMuted(next);
    try { localStorage.setItem('yumix_sound_muted', next ? '1' : '0'); } catch { /* noop */ }
    if (!next) {
      // Preview the cue when re-enabling
      try {
        const { playCelebrationCue } = await import('../../utils/celebrationSound');
        playCelebrationCue();
      } catch { /* noop */ }
    }
  }, []);
  return (
    <Card className="bg-card border-border" data-testid="celebration-sound-card">
      <CardHeader>
        <CardTitle className="font-heading text-xl uppercase tracking-tight flex items-center gap-2">
          <Bell className="w-5 h-5 text-primary" />
          {fr ? 'Sons de célébration' : 'Celebration sounds'}
        </CardTitle>
        <CardDescription>
          {fr
            ? "Petit son discret quand tes coachs Extra Miles te félicitent pour un repas conforme OMS."
            : "Soft cue when your Extra Miles coaches celebrate a WHO-compliant meal."}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-sm font-medium">
              {fr ? 'Activer le son' : 'Enable sound'}
            </Label>
            <p className="text-xs text-muted-foreground mt-1">
              {fr ? "Joue un accord majeur doux (~0.5s)." : "Plays a soft major chord (~0.5s)."}
            </p>
          </div>
          <Switch
            checked={!muted}
            onCheckedChange={(checked) => handleToggle(!checked)}
            data-testid="celebration-sound-toggle"
          />
        </div>
      </CardContent>
    </Card>
  );
});

export default SettingsNotifications;
